import { gql } from "gql";
import {
  MeristemExperimentInfo,
  RecommendedBuyflowOffersInput,
  TrackingParams,
} from "gql/graphql";
import { getSurveyAnswers, useSurveyAnswers } from "src/hooks/survey/answers";
import { getURLParams } from "src/utils/urlParams";
import { getVisitedExperiments } from "src/utils/experiment";
import visitTracker from "@noom/visit-tracker";
import { apolloClient } from "./apolloClient";
import getStore from "src/utils/redux/store";
import { getEligibleMedProducts } from "src/utils/medEligibility";

export const RECOMMENDED_BUYFLOW_OFFERS = gql(`
  query RecommendedBuyflowOffers($input: RecommendedBuyflowOffersInput!) {
    recommendedBuyflowOffers(input: $input) {
      offers
      recommendationId
    }
  }
`);

function getTrackingParams(): TrackingParams {
  const urlParams = getURLParams();
  const visitTrackerParams = visitTracker.getTrackingParams();

  return {
    initialUrl: visitTrackerParams?.initialUrl,
    referrer: visitTrackerParams?.referrer,
    utmSource: urlParams.utm_source,
    utmMedium: urlParams.utm_medium,
    utmCampaign: urlParams.utm_campaign,
    utmContent: urlParams.utm_content,
    utmTerm: urlParams.utm_term,
    feature: urlParams.feature ? [urlParams.feature].flat() : [],
  };
}

function getExperimentsInfo(): MeristemExperimentInfo[] {
  const visitedExperiments = getVisitedExperiments();
  return visitedExperiments.map(([context, experiment]) => ({
    context,
    experimentName: experiment.experimentName,
    variationName: experiment.variationName,
  }));
}

function getFormattedSurveyAnswers(
  surveyAnswers: ReturnType<typeof useSurveyAnswers>
) {
  return Object.entries(surveyAnswers)
    .filter(([, value]) => value != null)
    .map(([questionId, answer]) => ({
      questionId,
      answerJson: JSON.stringify(answer),
    }));
}

export async function queryRecommendedBuyflowOffers() {
  const state = getStore().getState();
  const { userData } = state;
  const surveyAnswers = getSurveyAnswers(state);

  const trackingParams = getTrackingParams();
  const experiments = getExperimentsInfo();
  const formattedSurveyAnswers = getFormattedSurveyAnswers(surveyAnswers);

  const input: RecommendedBuyflowOffersInput = {
    recommendationType: "MED_PLAN_OFFER",
    userId: userData.user_id,
    surveyAnswers: formattedSurveyAnswers,
    trackingParams,
    experiments,
    eligibleSKUsForRecommendation: getEligibleMedProducts(),
  };

  return apolloClient.query({
    query: RECOMMENDED_BUYFLOW_OFFERS,
    variables: {
      input,
    },
  });
}
